import { useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Camera, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DENOMS = ["1", "2", "3", "5", "10", "20", "30", "50"];
const LD_COLS = [
  { key: "opening", label: "Opening", applied: false },
  { key: "delivery", label: "Delivery", applied: true },
  { key: "tkt_add", label: "Tkt.Add", applied: true },
  { key: "return", label: "Return", applied: true },
  { key: "lvm1", label: "LVM-1", applied: true },
  { key: "lvm2", label: "LVM-2", applied: true },
  { key: "ending", label: "Ending", applied: true },
];

// "Scan Sheet" toolbar button: upload a photo of the handwritten worksheet,
// AI reads ONLY the Closing grid + Lottery Data table, user reviews, then
// onApply(result) pre-fills the entry form (user still saves normally).
export const SheetScanButton = ({ onApply }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Please choose a JPEG, PNG or WEBP photo");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/entry/scan-sheet`, fd, { timeout: 120000 });
      if (res.data.note) toast.warning(res.data.note, { duration: 8000 });
      setResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not read the photo — try again");
    } finally {
      setBusy(false);
    }
  };

  const closingRows = result ? DENOMS.filter((d) => (result.closing?.[d] || []).some((v) => v)) : [];
  const ldRows = result ? DENOMS.filter((d) => Object.values(result.lottery_data?.[d] || {}).some((v) => v)) : [];

  return (
    <>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="sheet-scan-input" />
      <Button
        variant="outline"
        size="sm"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        className="btn-3d"
        title="Photograph the handwritten worksheet — AI fills Closing numbers & Lottery Data for review"
        data-testid="sheet-scan-btn"
      >
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Camera className="w-4 h-4 mr-1" />}
        <span className="text-xs">{busy ? "Reading…" : "Scan Sheet"}</span>
      </Button>

      <Dialog open={!!result} onOpenChange={(o) => { if (!o) setResult(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="sheet-scan-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono">Review scanned values</DialogTitle>
            <DialogDescription className="font-mono text-xs">
              Read from your photo: the <strong>Closing</strong> grid and the <strong>Lottery Data</strong> table only.
              Apply fills the form — you can still edit anything before saving.
            </DialogDescription>
          </DialogHeader>

          {result && (
            <div className="space-y-4">
              <div>
                <p className="font-mono text-xs font-bold mb-1">Closing numbers → Running Numbers grid</p>
                {closingRows.length === 0 ? (
                  <p className="font-mono text-xs text-muted-foreground" data-testid="sheet-scan-closing-empty">Nothing readable found in the Closing grid.</p>
                ) : (
                  <div className="space-y-1" data-testid="sheet-scan-closing">
                    {closingRows.map((d) => (
                      <div key={d} className="flex flex-wrap items-center gap-1" data-testid={`sheet-scan-closing-${d}`}>
                        <span className="font-mono text-xs font-bold text-primary w-8">${d}</span>
                        {(result.closing[d] || []).map((v, i) => (
                          <span key={i} className={`font-mono text-xs px-1.5 py-0.5 rounded ${v ? "bg-primary/15" : "bg-muted text-muted-foreground"}`}>
                            {v || "·"}
                          </span>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <p className="font-mono text-xs font-bold mb-1">Lottery Data → Inventory (Opening shown for reference only)</p>
                {ldRows.length === 0 ? (
                  <p className="font-mono text-xs text-muted-foreground" data-testid="sheet-scan-ld-empty">Nothing readable found in the Lottery Data table.</p>
                ) : (
                  <div className="overflow-x-auto" data-testid="sheet-scan-ld">
                    <table className="w-full font-mono text-xs">
                      <thead>
                        <tr className="text-left text-muted-foreground">
                          <th className="p-1">Type</th>
                          {LD_COLS.map((c) => <th key={c.key} className={`p-1 ${c.applied ? "" : "opacity-50"}`}>{c.label}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {ldRows.map((d) => (
                          <tr key={d} className="border-t border-border/40" data-testid={`sheet-scan-ld-${d}`}>
                            <td className="p-1 font-bold text-primary">${d}</td>
                            {LD_COLS.map((c) => (
                              <td key={c.key} className={`p-1 ${c.applied ? "" : "opacity-50 line-through"}`}>
                                {result.lottery_data[d]?.[c.key] || "—"}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setResult(null)} data-testid="sheet-scan-cancel">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button
              className="btn-primary"
              disabled={closingRows.length === 0 && ldRows.length === 0}
              onClick={() => { onApply(result); setResult(null); }}
              data-testid="sheet-scan-apply"
            >
              <Check className="w-4 h-4 mr-1" /> Apply to entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SheetScanButton;
